/**
 * TierCard - 等级体系 v3 (LevelUp 2.0) 展示卡片
 * 展示：当前等级 + 月度积分进度 + 距下一段位 + 月度结算倒计时
 *
 * Props:
 *   userId: string  必填，当前用户 ID
 *   compact: boolean  可选，紧凑模式（在 ProfileHeader 内嵌时使用）
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  Paper,
  Box,
  Typography,
  LinearProgress,
  Chip,
  Tooltip,
  IconButton,
  CircularProgress,
  Skeleton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  EmojiEvents as EmojiEventsIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  Schedule as ScheduleIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { keyframes } from '@emotion/react';

import { getMyTier, codeToLabel, tierFromCode, POINTS_PER_LEVEL } from '../../services/tierApi';

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const TierCard = ({ userId, compact = false }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [tier, setTier] = useState(null);
  const [error, setError] = useState(null);

  const fetchTier = useCallback(async (silent = false) => {
    if (!userId) return;
    if (silent) setRefreshing(true);
    else setLoading(true);
    try {
      const res = await getMyTier(userId);
      if (res?.success) {
        setTier(res.data);
        setError(null);
      } else {
        setError(res?.message || '加载失败');
      }
    } catch (e) {
      setError(e?.response?.data?.message || e.message || '网络错误');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchTier(false);
  }, [fetchTier]);

  if (loading) {
    return (
      <Paper sx={{ p: isMobile ? 2 : 3, borderRadius: 2 }}>
        <Skeleton variant="text" width="40%" height={32} />
        <Skeleton variant="rectangular" width="100%" height={120} sx={{ mt: 2, borderRadius: 1 }} />
      </Paper>
    );
  }

  if (error || !tier) {
    return (
      <Paper sx={{ p: isMobile ? 2 : 3, borderRadius: 2, textAlign: 'center' }}>
        <Typography color="text.secondary" variant="body2">
          {error || '暂无等级数据'}
        </Typography>
        <IconButton size="small" onClick={() => fetchTier(false)} sx={{ mt: 1 }}>
          <RefreshIcon fontSize="small" />
        </IconButton>
      </Paper>
    );
  }

  const tierCfg = tierFromCode(tier.current_code);
  const monthlyPoints = tier.monthly_points || 0;
  const monthlyRequired = tier.monthly_required || POINTS_PER_LEVEL;
  const progressPct = Math.min(100, (monthlyPoints / monthlyRequired) * 100);
  const remainingDays = tier.month_remaining_days || 0;
  const pointsToNext = tier.points_to_next_level ?? Math.max(0, monthlyRequired - (monthlyPoints % monthlyRequired));

  // 段位渐变背景
  const gradient = `linear-gradient(135deg, ${tierCfg.color}33 0%, ${tierCfg.color}1a 50%, ${tierCfg.color}33 100%)`;
  const borderColor = tierCfg.color;

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2,
        overflow: 'hidden',
        background: gradient,
        backgroundSize: '200% 100%',
        animation: `${shimmer} 8s ease-in-out infinite`,
        border: `1px solid ${borderColor}66`,
        position: 'relative',
      }}
    >
      {/* 顶部：段位 icon + 等级标签 + 操作 */}
      <Box
        sx={{
          p: isMobile ? 2 : 2.5,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          borderBottom: `1px dashed ${borderColor}44`,
        }}
      >
        <Box
          sx={{
            width: isMobile ? 48 : 56,
            height: isMobile ? 48 : 56,
            borderRadius: '50%',
            background: `radial-gradient(circle, ${tierCfg.color}cc 0%, ${tierCfg.color}66 100%)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: isMobile ? 24 : 28,
            boxShadow: `0 4px 14px ${tierCfg.color}66`,
            flexShrink: 0,
          }}
        >
          {tierCfg.icon}
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant={isMobile ? 'subtitle1' : 'h6'} sx={{ fontWeight: 700, color: borderColor, lineHeight: 1.2 }}>
              {tierCfg.name}
            </Typography>
            <Chip
              size="small"
              label={codeToLabel(tier.current_code)}
              sx={{
                bgcolor: borderColor,
                color: '#fff',
                fontWeight: 700,
                height: 22,
                fontSize: 12,
              }}
            />
            {tier.is_max && (
              <Chip
                size="small"
                label="MAX"
                sx={{ bgcolor: '#ff4081', color: '#fff', fontWeight: 700, height: 20, fontSize: 11 }}
              />
            )}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.3 }}>
            周期 {tier.monthly_period}
            {tier.next_tier_name && ` · 距 ${tier.next_tier_name} 还差 ${tier.levels_to_next_tier} 级`}
          </Typography>
        </Box>

        <Tooltip title="刷新">
          <IconButton
            size="small"
            onClick={() => fetchTier(true)}
            disabled={refreshing}
            sx={{ color: borderColor }}
          >
            {refreshing ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Box>

      {/* 中部：月度积分进度 */}
      <Box sx={{ px: isMobile ? 2 : 2.5, pt: 2, pb: 1.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <TrendingUpIcon sx={{ fontSize: 18, color: borderColor }} />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              本月成长值
            </Typography>
            <Tooltip title={`每累计 ${monthlyRequired} 点升 1 小级，月底未达标降 1 小级`}>
              <InfoIcon sx={{ fontSize: 14, color: 'text.disabled', cursor: 'help' }} />
            </Tooltip>
          </Box>
          <Typography variant="body2" sx={{ fontWeight: 700, color: borderColor }}>
            {monthlyPoints} / {monthlyRequired}
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={progressPct}
          sx={{
            height: 10,
            borderRadius: 5,
            bgcolor: `${borderColor}22`,
            '& .MuiLinearProgress-bar': {
              borderRadius: 5,
              background: `linear-gradient(90deg, ${borderColor}cc 0%, ${borderColor} 100%)`,
            },
          }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.8 }}>
          {tier.is_max
            ? '已达最高等级 🎉'
            : `再获 ${pointsToNext} 点升至 ${tier.next_level_label || '下一级'}`}
        </Typography>
      </Box>

      {/* 底部：结算倒计时 + 累计 */}
      <Box
        sx={{
          px: isMobile ? 2 : 2.5,
          py: 1.2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: 'rgba(0,0,0,0.04)',
          borderTop: `1px dashed ${borderColor}33`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <ScheduleIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
          <Typography variant="caption" color="text.secondary">
            本月结算 <strong>{remainingDays}</strong> 天后
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <EmojiEventsIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
          <Typography variant="caption" color="text.secondary">
            累计 <strong>{tier.total_points || 0}</strong>
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};

export default TierCard;
